import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AppService } from '@App';
import { IConfigService } from '@Config';
import { BaseService } from '@Services';
import moment from 'moment';
import { Observable, ReplaySubject } from 'rxjs';
import { SubSink } from 'subsink';

export interface CaseWorkbenchTaskNoteResponse {
    id: string;
    caseId: string;
    clientId: string;
    userDisplayName: string;
    userRole: string;
    message: string;
    isInternalNote: boolean;
    createdBy: string;
    createdOn: string;
    createdOnDisplay?: string;
    updatedBy?: string;
    updatedOn?: string;
    updatedOnDisplay?: string;
    taskName?: string;
    taskId?: string;
    subtitle?: string;
}

export interface CreateCaseWorkbenchTaskNoteRequest {
    id?: string;
    message: string;
    isInternalNote: boolean;
}

@Injectable()
export class UWTaskNotesDataService extends BaseService implements OnDestroy {
    publicApiBaseUrl: string;

    // Case Task Notes State
    private caseTaskNotesLoaded = false;
    private caseIdForLoadedCaseTaskNotes = '';
    private caseTaskNotesSub = new ReplaySubject<CaseWorkbenchTaskNoteResponse[]>(1);
    public caseTaskNotes$ = this.caseTaskNotesSub.asObservable();

    private subs = new SubSink();

    constructor(
        public appService: AppService,
        public httpClient: HttpClient,
        public configService: IConfigService
    ) {
        super(appService, configService, httpClient);
        this.publicApiBaseUrl = this.configService.getConfiguration().publicApiUrl;
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    private caseTaskNotesAreLoaded = (caseId: string) =>
        caseId === this.caseIdForLoadedCaseTaskNotes && this.caseTaskNotesLoaded;

    getTaskNotes(caseId: string, taskId: string): Observable<CaseWorkbenchTaskNoteResponse[]> {
        const clientId = this.appService.getClientId('');
        return super.getData<CaseWorkbenchTaskNoteResponse[]>(
            `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}/notes`
        );
    }

    reloadCaseTaskNotesByCaseId(caseId: string, hideWaiting = false): Observable<CaseWorkbenchTaskNoteResponse[]> {
        const clientId = this.appService.getClientId('');
        const url = `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/CaseTask/notes/internal`;

        const sub = super.getData<CaseWorkbenchTaskNoteResponse[]>(url, null, hideWaiting).subscribe({
            next: notes => {
                const sorted = [...notes].sort((a, b) =>
                    moment(b.createdOn).diff(moment(a.createdOn), 'seconds')
                );

                sorted.forEach(note => {
                    note.createdOnDisplay = moment.utc(note.createdOn).tz('America/Chicago').format('M/D/YYYY h:mm a');
                    if (note.updatedOn) {
                        note.updatedOnDisplay = moment.utc(note.updatedOn).tz('America/Chicago').format('M/D/YYYY h:mm a');
                    }
                });

                this.caseTaskNotesSub.next(sorted);
                this.caseTaskNotesLoaded = true;
                this.caseIdForLoadedCaseTaskNotes = caseId;
            },
            error: () => undefined,
            complete: () => {
                sub.unsubscribe();
            }
        });

        return this.caseTaskNotes$;
    }

    getCaseTaskNotesByCaseId(caseId: string): Observable<CaseWorkbenchTaskNoteResponse[]> {
        if (this.caseTaskNotesAreLoaded(caseId)) return this.caseTaskNotes$;

        return this.reloadCaseTaskNotesByCaseId(caseId);
    }

    addTaskNote(caseId: string, taskId: string, request: CreateCaseWorkbenchTaskNoteRequest): Observable<CaseWorkbenchTaskNoteResponse> {
        const clientId = this.appService.getClientId('');
        return super.postData<CaseWorkbenchTaskNoteResponse>(
            `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}/notes`,
            request
        );
    }
    
    updateTaskNote(caseId: string, taskId: string, request: CreateCaseWorkbenchTaskNoteRequest): Observable<CaseWorkbenchTaskNoteResponse[]> {
        const clientId = this.appService.getClientId('');
        return super.patchData<CaseWorkbenchTaskNoteResponse[]>(
            `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}/notes`,
            request
        );
    }
}
